import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  Autocomplete,
} from "@mui/material";
import {
  HiOutlinePlay,
  HiOutlineStop,
  HiOutlinePencil,
  HiOutlineTrash,
  HiChevronDown,
  HiChevronUp,
  HiOutlineInformationCircle,
} from "react-icons/hi2";
import { useNavigate } from "react-router-dom";
import authService from "../../services/authService";
import machineService from "../../services/machineService";
import masterDataService from "../../services/masterDataService";
import breakdownLogService from "../../services/breakdownLogService";
import oeeService from "../../services/oeeService";
import productService from "../../services/productService";
import skuService from "../../services/skuService";
import plantService from "../../services/plantService";
import lineService from "../../services/lineService";
import "./OperatorDashboard.css";

const OperatorDashboard = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [selectedShift, setSelectedShift] = useState("");
  const [shifts, setShifts] = useState([]);
  const [allMachines, setAllMachines] = useState([]);
  const [machines, setMachines] = useState([]);

  const [plants, setPlants] = useState([]);
  const [pmStandards, setPmStandards] = useState([]);
  const [lines, setLines] = useState([]);
  const [selectedPlant, setSelectedPlant] = useState("");
  const [selectedLines, setSelectedLines] = useState([]);

  const [products, setProducts] = useState([]);
  const [lossTypes, setLossTypes] = useState([]);
  const [lossSubTypes, setLossSubTypes] = useState([]);
  const [lossReasons, setLossReasons] = useState([]);
  const [machineStats, setMachineStats] = useState({});

  // Inline Runs Table State
  const [expandedRuns, setExpandedRuns] = useState({}); // { [machineId]: boolean }
  const [runsData, setRunsData] = useState({}); // { [machineId]: Run[] }
  const [runsLoading, setRunsLoading] = useState({}); // { [machineId]: boolean }
  const [shiftStatus, setShiftStatus] = useState({}); // { [machineId]: 'InProgress' | 'Submitted' }
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state
  const [activeMachine, setActiveMachine] = useState(null);
  const [modalType, setModalType] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedDate && selectedShift && machines.length > 0) {
      loadMachineStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedShift, machines]);

  useEffect(() => {
    const fetchLines = async () => {
      if (!selectedPlant) {
        setLines([]);
        setSelectedLines([]);
        return;
      }
      try {
        const linesData = await lineService.getAllLines(selectedPlant);
        setLines(linesData || []);
        // Auto-select all lines ONLY for single-plant users. Multi-plant users get empty selection.
        if (plants.length === 1 && linesData?.length > 0) {
          setSelectedLines(linesData.map(l => l.lineId));
        } else {
          setSelectedLines([]);
        }
      } catch (err) {
        console.error("Failed to load lines", err);
      }
    };
    fetchLines();
  }, [selectedPlant, plants.length]);

  useEffect(() => {
    let filtered = allMachines;
    if (selectedPlant) {
      filtered = filtered.filter(m => m.plantId === parseInt(selectedPlant));
    }
    if (selectedLines.length > 0) {
      filtered = filtered.filter(m => selectedLines.includes(m.lineId));
    }
    setMachines(filtered);
  }, [selectedPlant, selectedLines, allMachines]);

  const loadInitialData = async () => {
    setInitialLoading(true);
    setError(null);
    try {
      const tenantId = authService.getTenantId();
      const [
        shiftsData,
        machinesData,
        productsData,
        lossTypesData,
        lossSubTypesData,
        lossReasonsData,
        plantsData,
        pmStandardsData,
      ] = await Promise.all([
        masterDataService.getShifts(tenantId),
        machineService.getAllMachines(),
        productService.getAllProducts(),
        masterDataService.getLossTypes(tenantId),
        masterDataService.getLossSubTypes(tenantId),
        masterDataService.getLossReasons(tenantId),
        plantService.getAllPlants(),
        masterDataService.getPMStandards(tenantId),
      ]);

      setShifts(shiftsData || []);
      setAllMachines(machinesData || []);
      setProducts(productsData || []);
      setLossTypes(lossTypesData || []);
      setLossSubTypes(lossSubTypesData || []);
      setLossReasons(lossReasonsData || []);
      setPlants(plantsData || []);
      setPmStandards(pmStandardsData || []);

      if (shiftsData && shiftsData.length > 0) {
        setSelectedShift(shiftsData[0].shiftId);
      }

      // Auto-select plant based on priority
      // Auto-select plant ONLY for single-plant users
      if (plantsData?.length === 1) {
        setSelectedPlant(plantsData[0].plantId.toString());
      }

    } catch (err) {
      console.error("Failed to load initial data", err);
      setError("Failed to load initial data. Please refresh the page.");
    } finally {
      setInitialLoading(false);
    }
  };

  const loadMachineStats = async () => {
    if (machines.length === 0) return;
    setLoading(true);
    const stats = {};
    await Promise.all(
      machines.map(async (machine) => {
        try {
          // Run both in parallel; handle 404 gracefully for metrics
          const [activeRun, metrics] = await Promise.all([
            oeeService.getActiveRun(machine.machineId),
            oeeService
              .getShiftMetrics(machine.machineId, selectedShift, selectedDate)
              .catch(() => null),
          ]);

          stats[machine.machineId] = {
            oee: metrics?.oee ?? null,
            availability: metrics?.availability ?? null,
            performance: metrics?.performance ?? null,
            quality: metrics?.quality ?? null,
            status: activeRun ? "Running" : "Stopped",
            activeRunId: activeRun?.id ?? null,
            activeRun: activeRun ?? null,
            product: activeRun?.productName || null,
            // Shift summary
            totalProduced: metrics?.totalProduced ?? 0,
            totalRejects: metrics?.totalRejects ?? 0,
            totalRework: metrics?.totalRework ?? 0,
            totalPlannedDowntimeMinutes:
              metrics?.totalPlannedDowntimeMinutes ?? 0,
            totalUnplannedDowntimeMinutes:
              metrics?.totalUnplannedDowntimeMinutes ?? 0,
            shiftDurationMinutes: metrics?.shiftDurationMinutes ?? 0,
          };
        } catch (err) {
          console.error(
            `Failed to load stats for machine ${machine.machineName}`,
            err,
          );
          stats[machine.machineId] = { status: "Stopped", error: true };
        }
      }),
    );
    setMachineStats(stats);

    // Load shift submission status for each machine in parallel
    const statuses = {};
    await Promise.all(
      machines.map(async (machine) => {
        try {
          const s = await oeeService.getShiftStatus(
            machine.machineId,
            selectedShift,
            selectedDate,
          );
          statuses[machine.machineId] = s?.status ?? "InProgress";
        } catch {
          statuses[machine.machineId] = "InProgress";
        }
      }),
    );
    setShiftStatus(statuses);

    setLoading(false);
  };

  const handleOpenModal = (machine, type) => {
    setActiveMachine(machine);
    setModalType(type);
  };

  const handleCloseModal = async (refresh = false) => {
    const machineId = activeMachine?.machineId;
    setActiveMachine(null);
    setModalType(null);
    if (refresh) {
      loadMachineStats();
      // Refetch runs if expanded
      if (machineId && expandedRuns[machineId]) {
        setRunsLoading((prev) => ({ ...prev, [machineId]: true }));
        try {
          const runs = await oeeService.getShiftRuns(
            machineId,
            selectedShift,
            selectedDate,
          );
          setRunsData((prev) => ({ ...prev, [machineId]: runs || [] }));
        } catch (err) {
          console.error("Failed to refresh runs", err);
        } finally {
          setRunsLoading((prev) => ({ ...prev, [machineId]: false }));
        }
      }
    }
  };

  const handleDeleteRun = async (runId, machineId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this Production Run? This will also delete all associated downtime and quality logs. This action cannot be undone.",
      )
    ) {
      try {
        await oeeService.deleteRun(runId);
        // Refresh runs
        setRunsLoading((prev) => ({ ...prev, [machineId]: true }));
        const runs = await oeeService.getShiftRuns(
          machineId,
          selectedShift,
          selectedDate,
        );
        setRunsData((prev) => ({ ...prev, [machineId]: runs || [] }));
        setRunsLoading((prev) => ({ ...prev, [machineId]: false }));
        // Refresh metrics
        loadMachineStats();
      } catch (err) {
        console.error(err);
        alert("Failed to delete run. Please try again.");
      }
    }
  };

  const handleDeleteDowntime = async (downtimeId, machineId) => {
    if (window.confirm("Are you sure you want to delete this Downtime Log?")) {
      try {
        await oeeService.deleteDowntime(downtimeId);
        // Refresh runs (downtime is part of run details)
        setRunsLoading((prev) => ({ ...prev, [machineId]: true }));
        const runs = await oeeService.getShiftRuns(
          machineId,
          selectedShift,
          selectedDate,
        );
        setRunsData((prev) => ({ ...prev, [machineId]: runs || [] }));
        setRunsLoading((prev) => ({ ...prev, [machineId]: false }));
        // Refresh metrics
        loadMachineStats();
      } catch (err) {
        console.error(err);
        alert("Failed to delete downtime log. Please try again.");
      }
    }
  };

  const toggleRuns = async (machineId) => {
    setExpandedRuns((prev) => ({ ...prev, [machineId]: !prev[machineId] }));

    // If expanding and data not loaded, fetch it
    if (!expandedRuns[machineId] && !runsData[machineId]) {
      setRunsLoading((prev) => ({ ...prev, [machineId]: true }));
      try {
        const runs = await oeeService.getShiftRuns(
          machineId,
          selectedShift,
          selectedDate,
        );
        setRunsData((prev) => ({ ...prev, [machineId]: runs || [] }));
      } catch (err) {
        console.error("Failed to load runs inline", err);
      } finally {
        setRunsLoading((prev) => ({ ...prev, [machineId]: false }));
      }
    }
  };

  if (initialLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        <p className="mt-4 text-tertiary font-medium tracking-wide">
          Initializing Dashboard...
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page-bg/50 p-0 font-sans text-primary">
      {error && (
        <div
          className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded shadow-sm"
          role="alert"
        >
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
      )}

      <div className="page-header">
        <div className="header-content">
          <h1> Operator Dashboard</h1>
          <div className="flex flex-wrap gap-3 mt-2 items-center">

            <div className="relative">
              <select
                value={selectedPlant}
                onChange={(e) => setSelectedPlant(e.target.value)}
                className="appearance-none bg-card border border-gray-300 text-secondary py-2 px-4 pr-8 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm min-w-[150px]"
              >
                <option value="">All Plants</option>
                {plants.map((p) => (
                  <option key={p.plantId} value={p.plantId}>
                    {p.plantName}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-tertiary">
                <HiChevronDown className="h-4 w-4" />
              </div>
            </div>

            <div className="relative formcontroldwn">
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  multiple
                  displayEmpty
                  value={selectedLines}
                  onChange={(e) => setSelectedLines(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  input={<OutlinedInput className="bg-card border-gray-300 rounded-lg text-sm shadow-sm" sx={{ '.MuiOutlinedInput-notchedOutline': { border: 'none' }, border: '1px solid #d1d5db; border-color: var(--border-primary)' }} />}
                  renderValue={(selected) => {
                    if (selected.length === 0) return <span className="text-tertiary">All Lines</span>;
                    const selectedNames = lines.filter(l => selected.includes(l.lineId)).map(l => l.lineName);
                    return <span className="text-secondary truncate block max-w-[120px]">{selectedNames.join(', ')}</span>;
                  }}
                  MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                  disabled={!selectedPlant}
                >
                  {lines.map((l) => (
                    <MenuItem key={l.lineId} value={l.lineId}>
                      <Checkbox checked={selectedLines.indexOf(l.lineId) > -1} />
                      <ListItemText primary={l.lineName} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className="relative">
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="pl-4 pr-10 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
              />
            </div>

            <div className="relative">
              <select
                value={selectedShift}
                onChange={(e) => setSelectedShift(e.target.value)}
                className="appearance-none bg-card border border-gray-300 text-secondary py-2 px-4 pr-8 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm min-w-[200px]"
              >
                {shifts.length === 0 && (
                  <option value="" disabled>
                    No shifts found
                  </option>
                )}
                {shifts.map((s) => (
                  <option key={s.shiftId} value={s.shiftId}>
                    {s.shiftCode} ({s.startTime.slice(0, 5)} -{" "}
                    {s.endTime.slice(0, 5)})
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-tertiary">
                <svg
                  className="fill-current h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </div>
            </div>

            <button
              onClick={loadMachineStats}
              disabled={loading}
              className="bg-card border border-gray-300 hover:bg-page-bg text-secondary font-medium py-2 px-4 rounded-lg shadow-sm transition-all text-sm flex items-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <div className="animate-spin h-4 w-4 border-2 border-gray-500 border-t-transparent rounded-full"></div>
              ) : (
                "Refresh"
              )}
            </button>

            <button
              onClick={() => navigate("/home/oee/settings")}
              className="bg-card border border-gray-300 hover:bg-page-bg text-secondary font-medium py-2 px-4 rounded-lg shadow-sm transition-all text-sm flex items-center gap-2"
              title="OEE Settings"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
      {/* Header Controls */}
      {/* <div className="flex flex-col md:flex-row gap-4 mb-8 items-center bg-card p-4 rounded-xl shadow-sm border border-gray-100">
        <h1 className="text-2xl font-bold text-primary tracking-tight mr-auto">
          Operator Dashboard
        </h1>

        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="pl-4 pr-10 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
            />
          </div>

          <div className="relative">
            <select
              value={selectedShift}
              onChange={(e) => setSelectedShift(e.target.value)}
              className="appearance-none bg-card border border-gray-300 text-secondary py-2 px-4 pr-8 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm min-w-[200px]"
            >
              {shifts.length === 0 && (
                <option value="" disabled>
                  No shifts found
                </option>
              )}
              {shifts.map((s) => (
                <option key={s.shiftId} value={s.shiftId}>
                  {s.shiftCode} ({s.startTime.slice(0, 5)} -{" "}
                  {s.endTime.slice(0, 5)})
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-tertiary">
              <svg
                className="fill-current h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
              </svg>
            </div>
          </div>

          <button
            onClick={loadMachineStats}
            disabled={loading}
            className="bg-card border border-gray-300 hover:bg-page-bg text-secondary font-medium py-2 px-4 rounded-lg shadow-sm transition-all text-sm flex items-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <div className="animate-spin h-4 w-4 border-2 border-gray-500 border-t-transparent rounded-full"></div>
            ) : (
              "Refresh"
            )}
          </button>

          <button
            onClick={() => navigate("/home/oee/settings")}
            className="bg-card border border-gray-300 hover:bg-page-bg text-secondary font-medium py-2 px-4 rounded-lg shadow-sm transition-all text-sm flex items-center gap-2"
            title="OEE Settings"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </button>
        </div>
      </div> */}

      {/* Machines Stack */}
      <div className="flex flex-col gap-6">
        {machines.length === 0 && (
          <div className="bg-blue-50 text-blue-700 p-4 rounded-lg text-center">
            No available machines found.
          </div>
        )}

        {machines.map((machine) => {
          const stats = machineStats[machine.machineId] || {};
          const isRunning = stats.status === "Running";
          const isLoading = loading && !machineStats[machine.machineId];
          const activeColor = isRunning ? "bg-green-500" : "bg-red-500";
          const activeText = isRunning
            ? "text-green-700 bg-green-50"
            : "text-red-700 bg-red-50";
          const activeBorder = isRunning
            ? "border-green-500"
            : "border-red-500";

          const isOeeEntryAllowed = pmStandards.some(s => s.machineId === machine.machineId && s.isMachineShutdownRequired && s.isActive !== false);

          return (
            <div
              key={machine.machineId}
              className={`bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100 overflow-hidden relative ${isLoading ? "opacity-75" : ""}`}
            >
              {/* Status Indicator Bar */}
              <div
                className={`absolute left-0 top-0 bottom-0 w-2 ${activeColor} transition-colors duration-300`}
              ></div>

              <div className="p-6">
                <div className="flex flex-col lg:flex-row items-center gap-6">
                  {/* Machine Identity */}
                  <div className="w-full lg:w-1/4 pl-4">
                    <h2 className="text-xl font-bold text-primary leading-tight">
                      {machine.machineName}
                    </h2>
                    <p className="text-sm text-tertiary font-medium mt-1">
                      {machine.machineCode}
                    </p>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${activeText}`}
                      >
                        {isRunning ? "Running" : "Stopped"}
                        {isRunning && stats.activeRun && (
                          <span className="text-secondary font-normal normal-case ml-1 flex items-center gap-2">
                            — {stats.activeRun.productName || stats.product}
                            {stats.activeRun.skuCode && (
                              <span className="px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 border border-teal-200 text-[10px] font-bold">
                                {stats.activeRun.skuCode || stats.activeRun.SkuCode} {(stats.activeRun.cycleTime || stats.activeRun.CycleTime) ? `(${Number(stats.activeRun.cycleTime || stats.activeRun.CycleTime).toFixed(1)}s)` : ''}
                              </span>
                            )}
                          </span>
                        )}
                      </span>
                    </div>

                    {isRunning && stats.activeRun && (
                      <div className="mt-4 flex gap-2 text-xs">
                        <div className="px-2 py-1 rounded bg-green-50 text-green-700 border border-green-200 font-semibold shadow-sm">
                          Good: {stats.activeRun.totalProducedQuantity}
                        </div>
                        <div className="px-2 py-1 rounded bg-red-50 text-red-700 border border-red-200 font-semibold shadow-sm">
                          Rej: {stats.activeRun.totalRejects}
                        </div>
                        <div className="px-2 py-1 rounded bg-yellow-50 text-yellow-700 border border-yellow-200 font-semibold shadow-sm">
                          Rew: {stats.activeRun.totalRework}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Metrics Grid */}
                  <div className="flex-1 w-full grid grid-cols-4 gap-4 px-4 py-2 border-l border-r border-gray-300 bg-page-bg/30 rounded-lg text-xs lg:rounded-none lg:bg-transparent ">
                    <MetricBox label="OEE" value={stats.oee} highlight />
                    <MetricBox
                      label="Availability"
                      value={stats.availability}
                    />
                    <MetricBox label="Performance" value={stats.performance} />
                    <MetricBox label="Quality" value={stats.quality} />
                  </div>

                  {/* Actions */}
                  <div className="w-full lg:w-auto flex flex-col sm:flex-row lg:flex-col gap-2 justify-end min-w-[180px]">
                    {!isOeeEntryAllowed ? (
                      <div className="flex items-center justify-center gap-2 py-4 px-4 bg-page-bg border border-gray-200 rounded-lg h-full">
                        <span className="text-tertiary font-bold text-xs text-center">
                          OEE Entry Disabled<br/><span className="text-[10px] font-normal">(PM Shutdown Config Required)</span>
                        </span>
                      </div>
                    ) : shiftStatus[machine.machineId] === "Submitted" ? (
                      <>
                        <div className="flex items-center justify-center gap-2 py-2 px-4 bg-green-50 border border-green-300 rounded-lg">
                          <span className="text-green-600 font-bold text-sm">
                            ✓ Shift Submitted
                          </span>
                        </div>
                        <button
                          onClick={async () => {
                            await oeeService.reopenShift(
                              machine.machineId,
                              parseInt(selectedShift),
                              selectedDate,
                            );
                            setShiftStatus((prev) => ({
                              ...prev,
                              [machine.machineId]: "InProgress",
                            }));
                          }}
                          className="w-full text-xs text-gray-400 hover:text-secondary underline text-center"
                        >
                          Reopen
                        </button>
                      </>
                    ) : (
                      <>
                        {!isRunning ? (
                          <button
                            onClick={() => handleOpenModal(machine, "startRun")}
                            disabled={!selectedShift}
                            className="w-full text-sm bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow transition-colors flex items-center justify-center gap-2"
                          >
                            <HiOutlinePlay className="text-lg" /> Start
                            Production
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() =>
                                handleOpenModal(machine, "production")
                              }
                              className="w-full text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2 px-4 rounded-lg shadow transition-colors"
                            >
                              Add Production
                            </button>
                            <button
                              onClick={() =>
                                handleOpenModal(machine, "downtime")
                              }
                              className="w-full text-sm bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg shadow transition-colors flex items-center justify-center gap-2"
                            >
                              <HiOutlineStop className="text-lg" /> Add Downtime
                            </button>
                            <button
                              onClick={() => handleOpenModal(machine, "endRun")}
                              className="w-full bg-card text-sm border border-gray-300 hover:bg-page-bg text-secondary font-medium py-2 px-4 rounded-lg transition-colors"
                            >
                              End Run
                            </button>
                          </>
                        )}
                        {/* Edit Log — always visible when shift not submitted */}
                        <button
                          onClick={() => handleOpenModal(machine, "editLog")}
                          disabled={!selectedShift}
                          className="w-full bg-amber-50 border border-amber-300 hover:bg-amber-100 text-amber-800 font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          <HiOutlinePencil className="text-base" /> Edit Log
                        </button>
                        {/* Submit Shift — only show when no active run */}
                        {!isRunning && (
                          <button
                            onClick={async () => {
                              if (isRunning) {
                                alert(
                                  "Please end the active production run before submitting the shift.",
                                );
                                return;
                              }
                              await oeeService.submitShift(
                                machine.machineId,
                                parseInt(selectedShift),
                                selectedDate,
                              );
                              setShiftStatus((prev) => ({
                                ...prev,
                                [machine.machineId]: "Submitted",
                              }));
                            }}
                            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg shadow transition-colors flex items-center justify-center gap-2 text-sm"
                          >
                            ✓ Submit Shift
                          </button>
                        )}
                      </>
                    )}

                  </div>
                </div>

                {/* OEE Progress Bar */}
                <div className="d-flex p-4 justify-center items-center">
                  <span className="whitespace-nowrap"> OEE Progress :</span>
                  <div className="ml-2 w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${activeColor} transition-all duration-700 ease-out`}
                      style={{ width: `${Math.min(stats.oee || 0, 100)}%` }}
                    ></div>
                  </div>
                </div>

                {/* <button
                      onClick={() => toggleRuns(machine.machineId)}
                      className="mt-2 text-gray-400 hover:text-secondary self-center lg:self-end transition-colors"
                    >
                      {expandedRuns[machine.machineId] ? (
                        <HiChevronUp size={20} />
                      ) : (
                        <HiChevronDown size={20} />
                      )}
                    </button> */}
                <button
                  onClick={() => toggleRuns(machine.machineId)}
                  className="collapseButton"
                >
                  {expandedRuns[machine.machineId] ? (
                    <div>
                      <HiChevronUp size={20} /> Collapse Details
                    </div>
                  ) : (
                    <div>
                      <HiChevronDown size={20} /> View Details
                    </div>
                  )}
                </button>

                {/* Shift Summary Strip */}
                {(stats.shiftDurationMinutes > 0 ||
                  stats.totalProduced > 0) && (
                    <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-3 sm:grid-cols-6 gap-3">
                      {[
                        {
                          label: "Shift Duration",
                          value: `${Math.round(stats.shiftDurationMinutes || 0)} min`,
                          color: "text-gray-700",
                        },
                        {
                          label: "Planned DT",
                          value: `${Number(stats.totalPlannedDowntimeMinutes || 0).toFixed(1)} min`,
                          color: "text-blue-600",
                        },
                        {
                          label: "Unplanned DT",
                          value: `${Number(stats.totalUnplannedDowntimeMinutes || 0).toFixed(1)} min`,
                          color: "text-red-600",
                        },
                        {
                          label: "Total Produced",
                          value: stats.totalProduced || 0,
                          color: "text-green-700",
                        },
                        {
                          label: "Rejects",
                          value: stats.totalRejects || 0,
                          color: "text-red-500",
                        },
                        {
                          label: "Rework",
                          value: stats.totalRework || 0,
                          color: "text-yellow-600",
                        },
                      ].map(({ label, value, color }) => (
                        <div
                          key={label}
                          className="flex flex-col items-center text-center"
                        >
                          <span className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">
                            {label}
                          </span>
                          <span className={`text-sm font-bold mt-0.5 ${color}`}>
                            {value}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                {/* History Collapse */}
                {expandedRuns[machine.machineId] && (
                  <div className="mt-6 pt-6 border-t border-gray-100 animate-fadeIn">
                    <h3 className="text-sm font-bold text-secondary uppercase tracking-wide mb-4">
                      Run History
                    </h3>

                    {runsLoading[machine.machineId] ? (
                      <div className="flex justify-center p-4">
                        <div className="animate-spin h-6 w-6 border-2 border-blue-500 rounded-full border-t-transparent"></div>
                      </div>
                    ) : (runsData[machine.machineId] || []).length === 0 ? (
                      <p className="text-sm text-tertiary italic">
                        No history available for this shift.
                      </p>
                    ) : (
                      <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-page-bg">
                            <tr>
                              <th className="px-4 py-3 text-left font-medium text-tertiary uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-4 py-3 text-left font-medium text-tertiary uppercase tracking-wider">
                                Run / Product
                              </th>
                              <th className="px-4 py-3 text-left font-medium text-tertiary uppercase tracking-wider">
                                SKU / Cycle Time
                              </th>
                              <th className="px-4 py-3 text-left font-medium text-tertiary uppercase tracking-wider">
                                Start
                              </th>
                              <th className="px-4 py-3 text-left font-medium text-tertiary uppercase tracking-wider">
                                End
                              </th>
                              <th className="px-4 py-3 text-right font-medium text-tertiary uppercase tracking-wider">
                                Good
                              </th>
                              <th className="px-4 py-3 text-right font-medium text-tertiary uppercase tracking-wider">
                                Reject
                              </th>
                              <th className="px-4 py-3 text-right font-medium text-tertiary uppercase tracking-wider">
                                Rework
                              </th>
                              <th className="px-4 py-3 text-right font-medium text-tertiary uppercase tracking-wider">
                                <div className="flex items-center justify-end gap-1.5">
                                  Performance Loss (min)
                                  <Tooltip title={
                                    <div className="p-1 max-w-[280px]">
                                      <div className="font-bold mb-1 text-orange-300 border-b border-orange-400/30 pb-1">Performance Loss Formula</div>
                                      <div className="text-white text-xs mb-1">
                                        <span className="font-semibold text-gray-300">Run Duration</span> − <span className="font-semibold text-teal-300">(Total Units × Cycle Time)</span> − <span className="font-semibold text-red-300">Unplanned Downtime</span>
                                      </div>
                                      <div className="text-[10px] text-gray-400 mt-1 leading-tight italic">
                                        *Measured strictly during active run execution bounds. Total Units = Good + Reject + Rework.
                                      </div>
                                    </div>
                                  } arrow placement="top">
                                    <span className="text-orange-400 hover:text-orange-600 cursor-help flex items-center bg-orange-50 rounded-full p-0.5" title="View formula">
                                      <HiOutlineInformationCircle size={15} />
                                    </span>
                                  </Tooltip>
                                </div>
                              </th>
                              <th className="px-4 py-3 text-center font-medium text-tertiary uppercase tracking-wider">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-card divide-y divide-gray-200">
                            {(runsData[machine.machineId] || []).map((run) => (
                              <tr
                                key={run.id || run.runId}
                                className="hover:bg-page-bg transition-colors"
                              >
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <span
                                    className={`inline-block w-2.5 h-2.5 rounded-full ${run.status === "Active" ? "bg-green-500" : "bg-gray-300"}`}
                                    title={run.status}
                                  ></span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="font-medium text-primary">
                                    Run #{run.id || run.runId}
                                  </div>
                                  <div className="text-xs text-tertiary">
                                    {run.productName || "-"}
                                  </div>
                                </td>
                                {/* SKU / Cycle Time column */}
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {run.skuCode ? (
                                    <div className="flex flex-col gap-0.5">
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-teal-50 text-teal-700 border border-teal-200 w-fit">
                                        {run.skuCode}
                                      </span>
                                      {(run.cycleTime || run.CycleTime) ? (
                                        <span className="text-[11px] text-gray-400 font-medium">
                                          CT: <span className="text-secondary font-semibold">{Number(run.cycleTime || run.CycleTime).toFixed(1)}s</span>
                                        </span>
                                      ) : (
                                        <span className="text-[11px] text-gray-400">No CT set</span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400 text-xs">—</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-secondary">
                                  {new Date(run.startTime).toLocaleTimeString(
                                    [],
                                    { hour: "2-digit", minute: "2-digit" },
                                  )}
                                </td>
                                <td className="px-4 py-3 text-secondary">
                                  {run.endTime
                                    ? new Date(run.endTime).toLocaleTimeString(
                                      [],
                                      { hour: "2-digit", minute: "2-digit" },
                                    )
                                    : "-"}
                                </td>
                                <td className="px-4 py-3 text-right font-semibold text-primary">
                                  <div className="flex items-center justify-end gap-1.5">
                                    <span>{run.totalProducedQuantity}</span>
                                    {(() => {
                                      const ct = Number(run.cycleTime || run.CycleTime);
                                      if (!ct || ct <= 0) return null;
                                      
                                      const start = new Date(run.startTime);
                                      const end = run.endTime ? new Date(run.endTime) : new Date();
                                      let totalMins = (end - start) / 60000;
                                      if (totalMins < 0) totalMins = 0;
                                      const expected = Math.floor((totalMins * 60) / ct);

                                      return (
                                        <Tooltip title={
                                          <div className="p-1">
                                            <div className="font-bold mb-1 text-blue-300 border-b border-blue-400/30 pb-1">Expected Production</div>
                                            <div className="text-white">Expected Output: <span className="font-bold text-green-400">{expected}</span> units</div>
                                            <div className="text-xs text-gray-300 mt-1 italic">
                                              Based on gross duration ({Math.floor(totalMins)} min) ÷ Cycle Time ({ct}s)
                                            </div>
                                            <div className="text-[10px] text-gray-400 mt-1 leading-tight">
                                              *Does not subtract logged downtimes.
                                            </div>
                                          </div>
                                        } arrow placement="left">
                                          <span className="text-blue-400 hover:text-blue-600 cursor-help flex items-center bg-blue-50 rounded-full p-0.5" title="View expected output">
                                            <HiOutlineInformationCircle size={15} />
                                          </span>
                                        </Tooltip>
                                      );
                                    })()}
                                  </div>
                                </td>
                                <td
                                  className={`px-4 py-3 text-right font-medium ${run.totalRejects > 0 ? "text-red-600" : "text-gray-400"}`}
                                >
                                  {run.totalRejects}
                                </td>
                                <td
                                  className={`px-4 py-3 text-right font-medium ${run.totalRework > 0 ? "text-yellow-600" : "text-gray-400"}`}
                                >
                                  {run.totalRework}
                                </td>
                                <td className="px-4 py-3 text-right font-semibold text-orange-600">
                                  {(() => {
                                    if (!run.endTime) return "—";
                                    const start = new Date(run.startTime);
                                    const end = new Date(run.endTime);
                                    let durationMins = (end - start) / 60000;
                                    if (durationMins < 0) durationMins = 0;
                                    
                                    const ct = Number(run.cycleTime || run.CycleTime || 0);
                                    const totalUnits = (run.totalProducedQuantity || 0) + (run.totalRejects || 0) + (run.totalRework || 0);
                                    const idealTimeMins = ct > 0 ? (totalUnits * ct) / 60 : 0;
                                    
                                    // Sum unplanned downtime ONLY overlapping the strictly executing run Duration bounds
                                    const unplannedDT = (run.downtimeLogs || [])
                                      .filter(log => !log.isPlanned && log.lossTypeName?.toLowerCase() !== 'speed loss')
                                      .reduce((sum, log) => {
                                        const logStart = new Date(log.startTime).getTime();
                                        const logEnd = new Date(log.endTime || new Date()).getTime();
                                        const runStart = start.getTime();
                                        const runEnd = end.getTime();
                                        
                                        const overlapStart = Math.max(logStart, runStart);
                                        const overlapEnd = Math.min(logEnd, runEnd);
                                        
                                        let overlapMins = 0;
                                        if (overlapEnd > overlapStart) {
                                            overlapMins = (overlapEnd - overlapStart) / 60000;
                                        }
                                        return sum + overlapMins;
                                      }, 0);
                                      
                                    let speedLoss = durationMins - idealTimeMins - unplannedDT;
                                    if (speedLoss < 0 || ct === 0) speedLoss = 0;
                                    
                                    return speedLoss > 0 ? Math.round(speedLoss).toFixed(1) : "0.0";
                                  })()}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <button
                                    onClick={() =>
                                      handleDeleteRun(
                                        run.id || run.runId,
                                        machine.machineId,
                                      )
                                    }
                                    className="text-gray-400 hover:text-red-600 transition-colors p-1"
                                    title="Delete Run"
                                  >
                                    <HiOutlineTrash size={18} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Detailed Downtime Logs */}
                    {runsData[machine.machineId] &&
                      runsData[machine.machineId].length > 0 && (
                        <div className="mt-8">
                          <h3 className="text-sm font-bold text-secondary uppercase tracking-wide mb-4">
                            Detailed Downtime Logs
                          </h3>

                          {runsData[machine.machineId].some(
                            (r) => r.downtimeLogs && r.downtimeLogs.length > 0,
                          ) ? (
                            <div className="overflow-x-auto rounded-lg border border-gray-200">
                              <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-page-bg">
                                  <tr>
                                    <th className="px-4 py-3 text-left font-medium text-tertiary uppercase tracking-wider">
                                      Loss Type
                                    </th>
                                    <th className="px-4 py-3 text-left font-medium text-tertiary uppercase tracking-wider">
                                      Sub Type
                                    </th>
                                    <th className="px-4 py-3 text-left font-medium text-tertiary uppercase tracking-wider">
                                      Start
                                    </th>
                                    <th className="px-4 py-3 text-left font-medium text-tertiary uppercase tracking-wider">
                                      End
                                    </th>
                                    <th className="px-4 py-3 text-right font-medium text-tertiary uppercase tracking-wider">
                                      Duration (min)
                                    </th>
                                    <th className="px-4 py-3 text-left font-medium text-tertiary uppercase tracking-wider">
                                      Reason
                                    </th>
                                    <th className="px-4 py-3 text-center font-medium text-tertiary uppercase tracking-wider">
                                      Action
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-card divide-y divide-gray-200">
                                  {runsData[machine.machineId]
                                    .flatMap((r) => r.downtimeLogs || [])
                                    .sort(
                                      (a, b) =>
                                        new Date(b.startTime) -
                                        new Date(a.startTime),
                                    )
                                    .map((log) => (
                                      <tr
                                        key={log.id}
                                        className="hover:bg-page-bg transition-colors"
                                      >
                                        <td className="px-4 py-3 text-primary font-medium">
                                          <span className="flex items-center gap-1.5">
                                            {log.lossTypeName || "-"}
                                            {(log.lossTypeName
                                              ?.toLowerCase()
                                              .includes("speed") ||
                                              log.lossTypeName
                                                ?.toLowerCase()
                                                .includes("performance")) && (
                                                <span className="relative group inline-flex items-center">
                                                  <span className="w-4 h-4 rounded-full bg-orange-100 text-orange-600 text-[10px] font-bold flex items-center justify-center cursor-help select-none leading-none">
                                                    i
                                                  </span>
                                                  {/* Tooltip */}
                                                  <span className="absolute left-5 top-1/2 -translate-y-1/2 z-50 hidden group-hover:flex flex-col w-80 bg-gray-900 text-white text-xs rounded-lg shadow-xl p-3 gap-1.5">
                                                    <span className="font-semibold text-orange-300 text-[11px] uppercase tracking-wide">
                                                      Performance Loss Calculation
                                                    </span>
                                                    <span className="text-gray-300 leading-relaxed">
                                                      Performance Loss ={" "}
                                                      <span className="text-white font-medium">
                                                        Run Duration
                                                      </span>{" "}
                                                      −{" "}
                                                      <span className="text-white font-medium">
                                                        (Cycle Time × Units Produced)
                                                      </span>{" "}
                                                      −{" "}
                                                      <span className="text-white font-medium">
                                                        Unplanned Downtime
                                                      </span>
                                                    </span>
                                                    <span className="border-t border-gray-700 pt-1.5 text-gray-400 leading-relaxed">
                                                      Run Duration = End Time − Start Time
                                                    </span>
                                                    <span className="text-gray-400 leading-relaxed">
                                                      Cycle Time = Ideal time per unit (from SKU master)
                                                    </span>
                                                    <span className="text-gray-400 leading-relaxed">
                                                      Unplanned Downtime = Sum of unplanned losses for this run
                                                    </span>
                                                    <span className="border-t border-gray-700 pt-1.5 text-yellow-300 leading-relaxed text-[10px]">
                                                      Auto-calculated at end of run.
                                                      Unplanned downtime is excluded to avoid double-counting.
                                                    </span>
                                                    {/* Arrow */}
                                                    <span className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-gray-900" />
                                                  </span>
                                                </span>
                                              )}
                                          </span>
                                        </td>
                                        <td className="px-4 py-3 text-secondary">
                                          {log.lossSubTypeName || "-"}
                                        </td>
                                        <td className="px-4 py-3 text-secondary whitespace-nowrap">
                                          {log.startTime
                                            ? new Date(
                                              log.startTime,
                                            ).toLocaleTimeString([], {
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })
                                            : "-"}
                                        </td>
                                        <td className="px-4 py-3 text-secondary whitespace-nowrap">
                                          {log.endTime
                                            ? new Date(
                                              log.endTime,
                                            ).toLocaleTimeString([], {
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })
                                            : "-"}
                                        </td>
                                        <td className="px-4 py-3 text-right font-semibold text-primary">
                                          {Number(log.durationMinutes).toFixed(
                                            1,
                                          )}
                                        </td>
                                        <td className="px-4 py-3 text-secondary">
                                          {log.lossReasonName ||
                                            log.comments ||
                                            "-"}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                          <button
                                            onClick={() =>
                                              handleDeleteDowntime(
                                                log.id,
                                                machine.machineId,
                                              )
                                            }
                                            className="text-gray-400 hover:text-red-600 transition-colors p-1"
                                            title="Delete Downtime Log"
                                          >
                                            <HiOutlineTrash size={18} />
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className="text-sm text-tertiary italic bg-page-bg p-4 rounded-lg border border-gray-100">
                              No downtime recorded for the runs in this period.
                            </p>
                          )}
                        </div>
                      )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Action Modal - Kept separate for stability, but conceptually part of dashboard */}
      {modalType && activeMachine && (
        <ActionDialog
          open={!!modalType}
          onClose={handleCloseModal}
          type={modalType}
          machine={activeMachine}
          stats={machineStats[activeMachine.machineId] || {}}
          selectedDate={selectedDate}
          selectedShift={selectedShift}
          shifts={shifts}
          products={products}
          lossTypes={lossTypes}
          lossSubTypes={lossSubTypes}
          lossReasons={lossReasons}
        />
      )}
    </div>
  );
};

// ─── Formula Tooltip Data ─────────────────────────────────────────────────────
const METRIC_FORMULAS = {
  OEE: {
    accent: "#3b82f6",         // blue-500
    badgeBg: "#1d4ed8",
    icon: "📊",
    title: "OEE",
    subtitle: "Overall Equipment Effectiveness",
    formula: "OEE = Availability × Performance × Quality",
    vars: [
      { name: "Availability", desc: "How long the machine was running vs total shift time", color: "#4ade80" },
      { name: "Performance", desc: "How fast the machine ran vs its ideal speed", color: "#fbbf24" },
      { name: "Quality", desc: "Good parts produced vs total parts made", color: "#c084fc" },
    ],
    benchmark: "🏆 World-class OEE ≥ 85%",
  },
  Availability: {
    accent: "#22c55e",
    badgeBg: "#15803d",
    icon: "⏱️",
    title: "Availability",
    subtitle: "Machine Uptime Ratio",
    formula: "Availability = Operating Time ÷ Total Shift Time × 100",
    vars: [
      { name: "Operating Time", desc: "Shift Duration − All Downtime (planned + unplanned)", color: "#86efac" },
      { name: "Total Shift Time", desc: "Full shift clock time from start to end", color: "#6ee7b7" },
    ],
    benchmark: "🏆 Target Availability ≥ 90%",
  },
  Performance: {
    accent: "#f59e0b",
    badgeBg: "#b45309",
    icon: "⚡",
    title: "Performance",
    subtitle: "Speed Efficiency Ratio",
    formula: "Performance = (Produced × Ideal Cycle Time) ÷ (Operating Time × 60) × 100",
    vars: [
      { name: "Produced", desc: "Total units produced in the run", color: "#fde68a" },
      { name: "Ideal Cycle Time", desc: "Target time per unit from SKU master (seconds/unit)", color: "#fcd34d" },
      { name: "Operating Time", desc: "Actual running time in minutes", color: "#fbbf24" },
    ],
    benchmark: "🏆 Target Performance ≥ 95%",
  },
  Quality: {
    accent: "#a855f7",
    badgeBg: "#7e22ce",
    icon: "✅",
    title: "Quality",
    subtitle: "Good Parts Ratio",
    formula: "Quality = (Total Produced − Rejects) ÷ Total Produced × 100",
    vars: [
      { name: "Total Produced", desc: "All units produced (good + rejected)", color: "#d8b4fe" },
      { name: "Rejects", desc: "Units that failed quality inspection", color: "#f87171" },
    ],
    benchmark: "🏆 Target Quality ≥ 99%",
  },
};

// ─── Metric Display ───────────────────────────────────────────────────────────
const MetricBox = ({ label, value, highlight = false }) => {
  const [hovered, setHovered] = React.useState(false);
  const ref = React.useRef(null);
  const [tooltipStyle, setTooltipStyle] = React.useState({});
  const formula = METRIC_FORMULAS[label];

  const handleMouseEnter = () => {
    if (!formula || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    // Position tooltip above the element, centered
    const tooltipWidth = 360;
    let left = rect.left + rect.width / 2 - tooltipWidth / 2;
    // Clamp to viewport
    if (left < 8) left = 8;
    if (left + tooltipWidth > window.innerWidth - 8) left = window.innerWidth - tooltipWidth - 8;
    setTooltipStyle({
      position: "fixed",
      bottom: window.innerHeight - rect.top + 10,
      left,
      width: tooltipWidth,
      zIndex: 9999,
    });
    setHovered(true);
  };

  return (
    <div
      ref={ref}
      className="flex flex-col items-center justify-center p-2 relative select-none"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setHovered(false)}
      style={{ cursor: formula ? "help" : "default" }}
    >
      {/* Label row */}
      <span className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
        {label}
        {formula && (
          <span
            className="flex items-center justify-center rounded-full font-bold select-none text-white"
            style={{
              width: 14, height: 14,
              background: formula.accent,
              fontSize: 8,
              lineHeight: 1,
            }}
          >
            ?
          </span>
        )}
      </span>

      {/* Value */}
      <span
        className={`text-2xl lg:text-3xl font-bold tracking-tight transition-colors duration-150 ${highlight ? "text-blue-600" : "text-gray-800"
          }`}
        style={hovered && formula ? { color: formula.accent } : {}}
      >
        {value !== null && value !== undefined ? (
          value < 0 ? (
            "0.0%"
          ) : value > 100 ? (
            <Tooltip title="Value exceeds expected limit (&gt;100%)" arrow placement="top">
              <span className="text-[0.6rem] lg:text-[0.62rem] text-red-500 font-bold leading-none text-center block max-w-[90px] uppercase bg-red-50 p-1 rounded border border-red-200">
                Limit Exceeded (&gt;100%)
              </span>
            </Tooltip>
          ) : (
            `${Number(value).toFixed(1)}%`
          )
        ) : (
          "—"
        )}
      </span>

      {/* ── Premium Tooltip ─────────────────────────────────────────── */}
      {formula && hovered && (
        <div
          style={{
            ...tooltipStyle,
            background: "linear-gradient(145deg, #0f172a 0%, #1e293b 100%)",
            border: `1px solid ${formula.accent}40`,
            borderRadius: 14,
            boxShadow: `0 25px 60px rgba(0,0,0,0.55), 0 0 0 1px ${formula.accent}25`,
            padding: "16px 18px",
            display: "flex",
            flexDirection: "column",
            gap: 10,
            pointerEvents: "none",
          }}
        >
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{
              background: formula.badgeBg,
              borderRadius: 8,
              width: 34, height: 34,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 17, flexShrink: 0,
              boxShadow: `0 2px 8px ${formula.accent}55`,
            }}>
              {formula.icon}
            </span>
            <div>
              <div style={{ color: formula.accent, fontWeight: 800, fontSize: 13, letterSpacing: 0.5 }}>
                {formula.title}
              </div>
              <div style={{ color: "#94a3b8", fontSize: 10.5, marginTop: 1 }}>
                {formula.subtitle}
              </div>
            </div>
          </div>

          {/* Divider */}
          <div style={{ height: 1, background: `linear-gradient(to right, ${formula.accent}60, transparent)` }} />

          {/* Formula box */}
          <div style={{
            background: "#0f172a",
            border: `1px solid ${formula.accent}35`,
            borderRadius: 8,
            padding: "9px 12px",
          }}>
            <div style={{ color: "#64748b", fontSize: 9.5, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 5 }}>
              Formula
            </div>
            <div style={{
              fontFamily: "'Courier New', monospace",
              color: "#f1f5f9",
              fontSize: 11.5,
              fontWeight: 700,
              lineHeight: 1.6,
              wordBreak: "break-word",
            }}>
              {formula.formula}
            </div>
          </div>

          {/* Variables breakdown */}
          <div>
            <div style={{ color: "#64748b", fontSize: 9.5, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6 }}>
              Variables
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {formula.vars.map((v) => (
                <div key={v.name} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <span style={{
                    marginTop: 3,
                    width: 6, height: 6,
                    borderRadius: "50%",
                    background: v.color,
                    flexShrink: 0,
                    boxShadow: `0 0 4px ${v.color}`,
                  }} />
                  <div style={{ flex: 1 }}>
                    <span style={{ color: v.color, fontWeight: 700, fontSize: 11 }}>{v.name}</span>
                    <span style={{ color: "#94a3b8", fontSize: 10.5, marginLeft: 4 }}>— {v.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Benchmark */}
          <div style={{
            background: `${formula.accent}12`,
            border: `1px solid ${formula.accent}30`,
            borderRadius: 7,
            padding: "6px 10px",
            color: formula.accent,
            fontSize: 10.5,
            fontWeight: 600,
          }}>
            {formula.benchmark}
          </div>

          {/* Arrow pointing down toward the metric */}
          <div style={{
            position: "absolute",
            bottom: -7,
            left: "50%",
            transform: "translateX(-50%)",
            width: 0, height: 0,
            borderLeft: "7px solid transparent",
            borderRight: "7px solid transparent",
            borderTop: `7px solid ${formula.accent}40`,
          }} />
        </div>
      )}
    </div>
  );
};

// ─── Action Dialog ────────────────────────────────────────────────────────────
const ActionDialog = ({
  open,
  onClose,
  type,
  machine,
  stats,
  selectedDate,
  selectedShift,
  shifts,
  products,
  lossTypes,
  lossSubTypes,
  lossReasons,
}) => {
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [filteredSubTypes, setFilteredSubTypes] = useState([]);
  const [filteredReasons, setFilteredReasons] = useState([]);
  // SKU state for Start Run
  const [skus, setSkus] = useState([]);
  const [skusLoading, setSkusLoading] = useState(false);
  // Runs state for logs
  const [recentRuns, setRecentRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);

  const fetchRecentRuns = async () => {
    setRunsLoading(true);
    try {
      const runs = await oeeService.getRecentRuns(machine.machineId);
      setRecentRuns(runs || []);
    } catch (err) {
      console.error("Failed to load runs", err);
    } finally {
      setRunsLoading(false);
    }
  };

  // State for editLog
  const [editLogRuns, setEditLogRuns] = useState([]);
  const [editLogLoading, setEditLogLoading] = useState(false);
  const [editingDowntime, setEditingDowntime] = useState(null); // { log, fields }
  const [editingRun, setEditingRun] = useState(null); // { run, fields }
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  const fetchEditLogData = async () => {
    setEditLogLoading(true);
    setEditingDowntime(null);
    setEditingRun(null);
    setEditError(null);
    try {
      const runs = await oeeService.getShiftRuns(
        machine.machineId,
        selectedShift,
        selectedDate,
      );
      setEditLogRuns(runs || []);
    } catch (err) {
      console.error("Failed to load runs for edit", err);
    } finally {
      setEditLogLoading(false);
    }
  };

  const handleSaveDowntimeEdit = async () => {
    if (!editingDowntime) return;
    setEditSaving(true);
    setEditError(null);
    try {
      if (!editingDowntime.fields.lossTypeId)
        throw new Error("Please select a Loss Type.");
      
      if ((editingDowntime.filteredSubTypes || []).length > 0 && !editingDowntime.fields.lossSubTypeId)
        throw new Error("Please select a Loss Sub-Type.");
        
      if (!editingDowntime.fields.lossReasonId && !editingDowntime.fields.lossReasonInput)
        throw new Error("Please select or type a Loss Reason.");

      let finalLossReasonId = editingDowntime.fields.lossReasonId;
      if (!finalLossReasonId && editingDowntime.fields.lossReasonInput && editingDowntime.fields.lossReasonInput.trim()) {
        const tenantId = authService.getTenantId();
        const newRes = await masterDataService.createLossReason({
          tenantId: parseInt(tenantId),
          reason: editingDowntime.fields.lossReasonInput.trim(),
          lossTypeId: editingDowntime.fields.lossTypeId ? parseInt(editingDowntime.fields.lossTypeId) : null,
          lossSubTypeId: editingDowntime.fields.lossSubTypeId ? parseInt(editingDowntime.fields.lossSubTypeId) : null,
          status: "Active"
        });
        finalLossReasonId = newRes.lossReasonId || newRes.id;
      }
      
      const payload = {};
      if (editingDowntime.fields.lossTypeId) payload.lossTypeId = parseInt(editingDowntime.fields.lossTypeId);
      if (editingDowntime.fields.lossSubTypeId) payload.lossSubTypeId = parseInt(editingDowntime.fields.lossSubTypeId);
      if (finalLossReasonId) payload.lossReasonId = parseInt(finalLossReasonId);
      if (editingDowntime.fields.comments !== undefined) payload.comments = editingDowntime.fields.comments;
      await oeeService.updateDowntime(editingDowntime.log.id, payload);
      setEditingDowntime(null);
      await fetchEditLogData();
    } catch (err) {
      const msg = err.response?.data || err.message || "Failed to save.";
      setEditError(msg);
    } finally {
      setEditSaving(false);
    }
  };

  const handleSaveRunEdit = async () => {
    if (!editingRun) return;
    setEditSaving(true);
    setEditError(null);
    try {
      const payload = {};
      if (editingRun.fields.totalProducedQuantity !== undefined)
        payload.totalProducedQuantity = parseInt(editingRun.fields.totalProducedQuantity);
      if (editingRun.fields.rejectQuantity !== undefined)
        payload.rejectQuantity = parseInt(editingRun.fields.rejectQuantity);
      if (editingRun.fields.reworkQuantity !== undefined)
        payload.reworkQuantity = parseInt(editingRun.fields.reworkQuantity);
      await oeeService.updateRun(editingRun.run.id || editingRun.run.runId, payload);
      setEditingRun(null);
      await fetchEditLogData();
    } catch (err) {
      const msg = err.response?.data || err.message || "Failed to save.";
      setEditError(msg);
    } finally {
      setEditSaving(false);
    }
  };

  // Reset on open
  useEffect(() => {
    setSubmitError(null);
    setSkus([]);
    setRecentRuns([]);

    if (["downtime", "startRun", "endRun"].includes(type)) {
      const now = new Date();
      const localIso = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);

      if (type === "downtime") {
        let defaultStart = now
          .toLocaleTimeString("en-GB", { hour12: false })
          .slice(0, 5);
        let defaultEnd = now
          .toLocaleTimeString("en-GB", { hour12: false })
          .slice(0, 5);

        // If we have a calculated minTime (s) from the render scope, we should respect it.
        // However, 'minTime' is calculated during render, which happens after this effect.
        // We need to replicate the calculation here or access the values if possible.
        // Since we can't easily access render variables in this effect, we'll re-calculate briefly

        if (machine && shifts && selectedShift) {
          const currentShift = shifts.find(
            (s) => s.shiftId === parseInt(selectedShift),
          );
          if (currentShift) {
            let s = currentShift.startTime.slice(0, 5);
            const e = currentShift.endTime.slice(0, 5);

            if (stats.activeRun?.startTime) {
              const runStart = new Date(stats.activeRun.startTime);
              const rTime = runStart
                .toLocaleTimeString("en-GB", { hour12: false })
                .slice(0, 5);
              const isNight = s > e;
              if (!isNight) {
                if (rTime > s && rTime < e) s = rTime;
              } else {
                if (rTime >= s) s = rTime;
                else if (rTime <= e) s = rTime;
              }
            }

            // Snap defaultStart to s if 'now' is earlier (or out of bounds)
            // Simple check for day shift logic:
            if (s <= e) {
              if (defaultStart < s) defaultStart = s;
            } else {
              // Night shift logic
              // s=22:00, e=06:00
              // Valid: 22:00..23:59, 00:00..06:00
              // If defaultStart is 21:00 -> invalid, use 22:00?
              // If defaultStart is 07:00 -> invalid?
              // Actually, simpler logic: if s (e.g. 06:15) is set, and we are at 06:10,
              // defaultStart should be 06:15.

              // Let's just use the `minTime` logic roughly.
              // But more importantly, the USER said "auto updated".
              // So if RunStart=06:15, form start time MUST BE 06:15 (or later).

              // If defaultStart (Now) is BEFORE s, use s.
              // (Assuming s isn't crossing midnight weirdly relative to Now)

              // Quick fix for the specific requested case (Startup Loss):
              // If s > now (in minutes on same day), use s?
              // Or if s is 06:15, and now is 06:20, use 06:20.
              // But if s is 06:15 and "start up 15 min", maybe they WANT it to default to 06:15
              // to log that 15 min gap?
              // "sppose the shift is started delayed (ex. 6.15) which is our start up loss then the time must be show in range of 6.15 to end"
              // Wait, if shift is delayed, does it mean Run started at 6:15? Yes.
              // So downtime can ONLY happen AFTER 6:15.
              // So the form should default to "Now", but bounded by 6:15.

              if (s <= e) {
                if (defaultStart < s) defaultStart = s;
              }
            }
          }
        }

        setFormData({
          startTime: defaultStart,
          endTime: defaultEnd,
          lossTypeId: "",
          lossSubTypeId: "",
          lossReasonId: "",
          comments: "",
        });
        setFilteredSubTypes([]);
        setFilteredReasons([]);
      } else if (type === "startRun") {
        setFormData({
          startTime: now
            .toLocaleTimeString("en-GB", { hour12: false })
            .slice(0, 5),
        }); // HH:mm
      } else if (type === "endRun") {
        setFormData({
          endTime: now
            .toLocaleTimeString("en-GB", { hour12: false })
            .slice(0, 5),
        }); // HH:mm
      }
    } else if (type === "logs") {
      fetchRecentRuns();
    } else if (type === "editLog") {
      fetchEditLogData();
    } else {
      setFormData({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type]);

  // Fetch SKUs when a product is selected in startRun
  const handleProductChange = async (productId) => {
    set("productId", productId);
    set("skuId", ""); // reset SKU
    setSkus([]);
    if (!productId) return;
    setSkusLoading(true);
    try {
      const data = await skuService.getActiveSkusByProduct(productId);
      setSkus(data || []);
    } catch (err) {
      console.error("Failed to load SKUs", err);
      setSkus([]);
    } finally {
      setSkusLoading(false);
    }
  };

  const set = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleLossTypeChange = (typeId) => {
    set("lossTypeId", typeId);
    set("lossSubTypeId", "");
    set("lossReasonId", "");
    set("lossReasonInput", "");
    
    const subTypes = lossSubTypes.filter(
      (s) => s.lossTypeId == typeId || s.LossTypeId == typeId,
    );
    setFilteredSubTypes(subTypes);

    if (typeId) {
      setFilteredReasons(
        lossReasons.filter(
          (r) => r.lossTypeId == typeId || r.LossTypeId == typeId,
        ),
      );
    } else {
      setFilteredReasons([]);
    }
  };

  const handleLossSubTypeChange = (subTypeId) => {
    set("lossSubTypeId", subTypeId);
    set("lossReasonId", "");
    set("lossReasonInput", "");
    if (subTypeId) {
      setFilteredReasons(
        lossReasons.filter(
          (r) => r.lossSubTypeId == subTypeId || r.LossSubTypeId == subTypeId,
        ),
      );
    } else if (formData.lossTypeId) {
      setFilteredReasons(
        lossReasons.filter(
          (r) =>
            r.lossTypeId == formData.lossTypeId ||
            r.LossTypeId == formData.lossTypeId,
        ),
      );
    } else {
      setFilteredReasons([]);
    }
  };

  const resolveShiftDateTime = (timeStr) => {
    if (!timeStr) throw new Error("Time is required");

    const shift = shifts.find((s) => s.shiftId === parseInt(selectedShift));
    if (!shift) return new Date(`${selectedDate}T${timeStr}`).toISOString();

    const [h, m] = timeStr.split(":").map(Number);
    const inputMinutes = h * 60 + m;

    const [sH, sM] = shift.startTime.split(":").map(Number);
    const startMinutes = sH * 60 + sM;
    const [eH, eM] = shift.endTime.split(":").map(Number);
    const endMinutes = eH * 60 + eM;

    const isNightShift = endMinutes < startMinutes;
    let dayOffset = 0;

    if (!isNightShift) {
      if (inputMinutes < startMinutes || inputMinutes > endMinutes) {
        throw new Error(
          `Time must be between ${shift.startTime.slice(0, 5)} and ${shift.endTime.slice(0, 5)}`,
        );
      }
    } else {
      if (inputMinutes >= startMinutes) {
        dayOffset = 0;
      } else if (inputMinutes <= endMinutes) {
        dayOffset = 1;
      } else {
        throw new Error(
          `Time must be between ${shift.startTime.slice(0, 5)} and ${shift.endTime.slice(0, 5)} (Next Day)`,
        );
      }
    }

    // Parse selectedDate string directly to avoid UTC/Local shifts
    const [yStr, mStr, dStr] = selectedDate.split("-");
    // Create Local Date
    const localDate = new Date(
      parseInt(yStr),
      parseInt(mStr) - 1,
      parseInt(dStr) + dayOffset,
      h,
      m,
      0,
    );

    // Validate against Active Run Start if present (Startup Loss / Delayed Start scenario)
    if (stats.activeRun?.startTime) {
      const runStart = new Date(stats.activeRun.startTime);
      // Create a comparison date that ignores seconds/millis for fair comparison with HH:mm input
      const runStartCompare = new Date(runStart);
      runStartCompare.setSeconds(0, 0);

      if (localDate < runStartCompare) {
        throw new Error(
          `Time cannot be before Run Start Time (${runStart.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })})`,
        );
      }
    }

    return localDate.toISOString();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (type === "startRun") {
        if (!formData.productId) throw new Error("Please select a product.");
        await oeeService.startRun({
          machineId: machine.machineId,
          productId: parseInt(formData.productId),
          skuId: formData.skuId ? parseInt(formData.skuId) : null,
          shiftId: parseInt(selectedShift),
          startTime: resolveShiftDateTime(formData.startTime),
        });
      } else if (type === "production") {
        const goodQty = parseInt(formData.goodQuantity || 0);
        const rejectQty = parseInt(formData.rejectQuantity || 0);
        const reworkQty = parseInt(formData.reworkQuantity || 0);

        if (goodQty <= 0 && rejectQty <= 0 && reworkQty <= 0)
          throw new Error("Please enter at least one quantity.");

        // specific logic: updateRun handles all three if provided
        const currentRun = await oeeService.getRun(stats.activeRunId);

        const payload = {};
        // Calculate new cumulative totals (since backend expects totals or handles deltas via total comparison)
        // Backend logic: delta = (newTotal - currentTotal). If delta > 0, log it.
        // So we send: currentTotal + inputDelta

        if (goodQty > 0) {
          payload.totalProducedQuantity =
            (currentRun.totalProducedQuantity || 0) + goodQty;
        }
        if (rejectQty > 0) {
          payload.rejectQuantity = (currentRun.totalRejects || 0) + rejectQty;
          // Only send reason if rejecting
          if (formData.rejectReason)
            payload.rejectReason = formData.rejectReason;
        }
        if (reworkQty > 0) {
          payload.reworkQuantity = (currentRun.totalRework || 0) + reworkQty;
        }

        if (Object.keys(payload).length > 0) {
          await oeeService.updateRun(stats.activeRunId, payload);
        }
      } else if (type === "downtime") {
        if (!formData.startTime || !formData.endTime)
          throw new Error("Please enter start and end times.");
        
        if (!formData.lossTypeId)
          throw new Error("Please select a Loss Type.");
        
        if (filteredSubTypes.length > 0 && !formData.lossSubTypeId)
          throw new Error("Please select a Loss Sub-Type.");
          
        let finalLossReasonId = formData.lossReasonId;
        
        // If no ID selected but custom text entered, create NEW reason
        if (!finalLossReasonId && formData.lossReasonInput && formData.lossReasonInput.trim()) {
          try {
            const tenantId = authService.getTenantId();
            const newRes = await masterDataService.createLossReason({
              tenantId: parseInt(tenantId),
              reason: formData.lossReasonInput.trim(),
              lossTypeId: formData.lossTypeId ? parseInt(formData.lossTypeId) : null,
              lossSubTypeId: formData.lossSubTypeId ? parseInt(formData.lossSubTypeId) : null,
              status: "Active"
            });
            finalLossReasonId = newRes.lossReasonId || newRes.id;
          } catch (createErr) {
            console.error("Failed to auto-create loss reason", createErr);
            throw new Error("Failed to create new loss reason. Use an existing one or try again.");
          }
        }

        if (!finalLossReasonId)
          throw new Error("Please select or type a Loss Reason.");

        const downtimeData = {
          productionRunId: stats.activeRunId,
          machineId: machine.machineId,
          lossTypeId: formData.lossTypeId ? parseInt(formData.lossTypeId) : null,
          lossSubTypeId: formData.lossSubTypeId
            ? parseInt(formData.lossSubTypeId)
            : null,
          lossReasonId: finalLossReasonId ? parseInt(finalLossReasonId) : null,
          startTime: resolveShiftDateTime(formData.startTime),
          endTime: resolveShiftDateTime(formData.endTime),
          comments: formData.comments || "",
        };

        await oeeService.logDowntime(downtimeData);

        // If it's a Breakdown Loss, also add to Breakdown Log
        const selectedType = lossTypes.find(
          (t) => (t.lossTypeId || t.id) == formData.lossTypeId,
        );
        if (
          selectedType &&
          (selectedType.lossTypeName || selectedType.name || "")
            .toLowerCase()
            .includes("breakdown")
        ) {
          try {
            await breakdownLogService.createBreakdownLog({
              machineId: machine.machineId,
              plantId: machine.plantId || null,
              breakdownStartTime: downtimeData.startTime,
              breakdownEndTime: downtimeData.endTime,
              failureDescription:
                downtimeData.comments || "Logged from OEE Dashboard",
              failureCategory: "Breakdown",
              reportedBy: authService.getUserId() || 0,
              statusId: null, // Let backend default
            });
          } catch (blogErr) {
            console.error("Failed to add to Breakdown Log", blogErr);
            // We don't throw here to avoid failing the OEE downtime log success
          }
        }
      } else if (type === "endRun") {
        const resolvedEndTime = resolveShiftDateTime(formData.endTime);

        if (stats.activeRun?.startTime) {
          const start = new Date(stats.activeRun.startTime);
          const end = new Date(resolvedEndTime);
          if (end <= start) {
            throw new Error(
              `End time (${end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}) must be after Start time (${start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })})`,
            );
          }
        }

        await oeeService.endRun(stats.activeRunId, {
          totalProducedQuantity: stats.activeRun?.totalProducedQuantity || 0,
          status: "Completed",
          endTime: resolvedEndTime,
        });
      }

      onClose(true); // true = refresh stats
    } catch (err) {
      console.error("Action failed", err);
      // Extract the message from an Axios error response (like our BadRequest string)
      const errorMessage =
        err.response?.data ||
        err.message ||
        "An error occurred. Please try again.";
      setSubmitError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const titles = {
    startRun: `Start Production — ${machine.machineName}`,
    production: `Add Production — ${machine.machineName}`,
    downtime: `Add Downtime — ${machine.machineName}`,
    endRun: `End Run — ${machine.machineName}`,
    logs: `Logs — ${machine.machineName}`,
    editLog: `Edit Logs — ${machine.machineName}`,
  };

  // Calculate constraints
  // Calculate constraints
  const currentShift = shifts.find(
    (s) => s.shiftId === parseInt(selectedShift),
  );
  let minTime = undefined,
    maxTime = undefined;
  if (currentShift) {
    let s = currentShift.startTime.slice(0, 5);
    const e = currentShift.endTime.slice(0, 5);

    // Adjust minTime if Run started AFTER Shift Start (Delayed Start)
    if (stats.activeRun?.startTime) {
      const runStart = new Date(stats.activeRun.startTime);
      const rTime = runStart
        .toLocaleTimeString("en-GB", { hour12: false })
        .slice(0, 5);

      // Check if rTime is "after" s within the context of the shift
      // Logic depends on whether shift wraps midnight
      const isNight = s > e;

      if (!isNight) {
        // Normal Day Shift (e.g. 06:00 to 14:00)
        if (rTime > s && rTime < e) {
          s = rTime;
        }
      } else {
        // Night Shift (e.g. 22:00 to 06:00)
        // Timeline: 22:00 ... 23:59 -> 00:00 ... 06:00
        // If rTime is >= 22:00, it's later than s.
        // If rTime is <= 06:00, it's MUCH later than s (next day).

        if (rTime >= s) {
          s = rTime; // e.g. Start 23:00 > 22:00
        } else if (rTime <= e) {
          s = rTime; // e.g. Start 01:00 > 22:00 (effectively)
          // If we set min="01:00" and max="06:00", it correctly limits to 01:00-06:00
          // And excludes 22:00-00:00. This is correct for "Delayed Start".
        }
      }
    }

    // Apply HTML5 time input constraints
    // Note: For crossing midnight (min > max), HTML5 input behavior varies.
    // Chrome supports min="22:00" max="06:00" as a valid range crossing midnight.
    // But if we adjusted s to "01:00", then min="01:00" max="06:00" is a simple range.
    minTime = s;
    maxTime = e;
  }

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth={type === "editLog" ? "md" : "sm"} fullWidth>
      <DialogTitle>{titles[type]}</DialogTitle>
      <DialogContent dividers>
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        {/* START RUN */}
        {type === "startRun" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {/* Product */}
            <FormControl fullWidth>
              <InputLabel>Product *</InputLabel>
              <Select
                value={formData.productId || ""}
                label="Product *"
                onChange={(e) => handleProductChange(e.target.value)}
              >
                <MenuItem value="" disabled>
                  Select a product
                </MenuItem>
                {products.map((p) => (
                  <MenuItem
                    key={p.productId || p.id}
                    value={p.productId || p.id}
                  >
                    {p.productName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* SKU — shown once a product is selected */}
            {formData.productId && (
              <Box>
                <FormControl fullWidth>
                  <InputLabel>
                    {skusLoading
                      ? "Loading SKUs…"
                      : skus.length === 0
                        ? "No SKUs for this product"
                        : "SKU (optional)"}
                  </InputLabel>
                  <Select
                    value={formData.skuId || ""}
                    label={
                      skusLoading
                        ? "Loading SKUs…"
                        : skus.length === 0
                          ? "No SKUs for this product"
                          : "SKU (optional)"
                    }
                    onChange={(e) => set("skuId", e.target.value)}
                    disabled={skusLoading || skus.length === 0}
                  >
                    <MenuItem value="">— No SKU —</MenuItem>
                    {skus.map((s) => (
                      <MenuItem key={s.skuId} value={s.skuId}>
                        {s.skuCode} — {s.skuName}
                        {s.cycleTime ? ` (CT: ${s.cycleTime}s)` : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Smart Suggestion Tooltip */}
                {formData.skuId && formData.startTime && currentShift && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip
                      arrow
                      placement="right"
                      title={(() => {
                        const sObj = skus.find(s => s.skuId === formData.skuId);
                        if (!sObj || !sObj.cycleTime) return "Select a SKU with cycle time to see expected output";

                        const [sh, sm] = formData.startTime.split(':').map(Number);
                        const [eh, em] = currentShift.endTime.slice(0, 5).split(':').map(Number);
                        let sMins = sh * 60 + sm;
                        let eMins = eh * 60 + em;
                        if (eMins < sMins) eMins += 1440;
                        const diffMins = eMins - sMins;
                        const expected = Math.floor((diffMins * 60) / sObj.cycleTime);

                        return (
                          <Box sx={{ p: 0.5 }}>
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', mb: 0.5, color: '#93c5fd' }}>
                              Production Suggestion
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', color: '#94a3b8', fontStyle: 'italic', mb: 1 }}>
                              Expected Output = (Run Time / Ideal Cycle Time)
                            </Typography>
                            <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', pt: 0.5, mt: 0.5 }}>
                              <Typography variant="caption" sx={{ display: 'block' }}>
                                Available Time: {diffMins} min
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block' }}>
                                Cycle Time: {sObj.cycleTime}s
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 'bold' }}>
                                Possible Production: {expected} units
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })()}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', color: 'info.main', cursor: 'help', fontSize: '0.75rem', fontWeight: 500 }}>
                        <HiOutlineInformationCircle style={{ fontSize: '1rem', marginRight: '4px' }} />
                        View Expected Output Recommendation
                      </Box>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            )}

            <Typography variant="caption" color="textSecondary">
              Machine: <strong>{machine.machineName}</strong>
              {formData.skuId &&
                skus.find((s) => s.skuId === formData.skuId)?.cycleTime
                ? ` | Cycle time: ${skus.find((s) => s.skuId === formData.skuId).cycleTime}s`
                : ""}
            </Typography>

            <TextField
              label="Start Time"
              type="time"
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: minTime, max: maxTime }}
              value={formData.startTime || ""}
              onChange={(e) => set("startTime", e.target.value)}
            />
          </Box>
        )}

        {/* ADD PRODUCTION */}
        {type === "production" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Good Quantity Produced"
              type="number"
              inputProps={{ min: 0 }}
              fullWidth
              value={formData.goodQuantity || ""}
              onChange={(e) => set("goodQuantity", e.target.value)}
            />
            <TextField
              label="Reject Quantity"
              type="number"
              inputProps={{ min: 0 }}
              fullWidth
              value={formData.rejectQuantity || ""}
              onChange={(e) => set("rejectQuantity", e.target.value)}
            />
            <TextField
              label="Rework Quantity"
              type="number"
              inputProps={{ min: 0 }}
              fullWidth
              value={formData.reworkQuantity || ""}
              onChange={(e) => set("reworkQuantity", e.target.value)}
            />
            {parseInt(formData.rejectQuantity || 0) > 0 && (
              <TextField
                label="Reject Reason"
                fullWidth
                value={formData.rejectReason || ""}
                onChange={(e) => set("rejectReason", e.target.value)}
              />
            )}
            <Typography variant="caption" color="textSecondary">
              These quantities will be added to the current run total.
            </Typography>
          </Box>
        )}

        {/* ADD DOWNTIME */}
        {type === "downtime" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Alert severity="info" sx={{ py: 0, alignItems: "center" }}>
              <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                Valid Time Range: {minTime} - {maxTime}
                {stats.activeRun?.startTime &&
                  ` (Run Started: ${new Date(stats.activeRun.startTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })})`}
              </Typography>
            </Alert>

            <TextField
              label="Start Time"
              type="time"
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: minTime, max: maxTime }}
              value={formData.startTime || ""}
              onChange={(e) => set("startTime", e.target.value)}
            />
            <TextField
              label="End Time"
              type="time"
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: minTime, max: maxTime }}
              value={formData.endTime || ""}
              onChange={(e) => set("endTime", e.target.value)}
            />
            <FormControl fullWidth>
              <InputLabel>Loss Type</InputLabel>
              <Select
                value={formData.lossTypeId || ""}
                label="Loss Type"
                onChange={(e) => handleLossTypeChange(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {lossTypes
                  .filter((t) => {
                    const name = (t.lossTypeName || t.name || "").trim().toLowerCase();
                    return name !== "change over loss" && name !== "reject & rework loss" && name !== "distribution losses";
                  })
                  .map((t) => (
                    <MenuItem
                      key={t.lossTypeId || t.id}
                      value={t.lossTypeId || t.id}
                    >
                      {t.lossTypeName || t.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl fullWidth disabled={!formData.lossTypeId || filteredSubTypes.length === 0}>
              <InputLabel>Loss Sub-Type</InputLabel>
              <Select
                value={formData.lossSubTypeId || ""}
                label="Loss Sub-Type"
                onChange={(e) => handleLossSubTypeChange(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {filteredSubTypes.map((s) => (
                  <MenuItem
                    key={s.lossSubTypeId || s.id || s.subTypeId}
                    value={s.lossSubTypeId || s.id || s.subTypeId}
                  >
                    {s.lossSubTypeName || s.name || s.subTypeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              freeSolo
              disabled={!formData.lossTypeId}
              options={filteredReasons}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.lossReasonName || option.name || option.reason || "";
              }}
              value={filteredReasons.find(r => (r.lossReasonId || r.id) == formData.lossReasonId) || formData.lossReasonInput || ""}
              onChange={(event, newValue) => {
                if (typeof newValue === 'string') {
                  const match = filteredReasons.find(r => (r.lossReasonName || r.name || r.reason) === newValue);
                  if (match) {
                    set("lossReasonId", match.lossReasonId || match.id);
                    set("lossReasonInput", match.lossReasonName || match.name || match.reason);
                  } else {
                    set("lossReasonId", "");
                    set("lossReasonInput", newValue);
                  }
                } else if (newValue) {
                  set("lossReasonId", newValue.lossReasonId || newValue.id);
                  set("lossReasonInput", newValue.lossReasonName || newValue.name || newValue.reason);
                } else {
                  set("lossReasonId", "");
                  set("lossReasonInput", "");
                }
              }}
              onInputChange={(event, newInputValue) => {
                const match = filteredReasons.find(r => (r.lossReasonName || r.name || r.reason) === newInputValue);
                if (match) {
                  set("lossReasonId", match.lossReasonId || match.id);
                } else {
                  set("lossReasonId", "");
                }
                set("lossReasonInput", newInputValue);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Loss Reason" fullWidth />
              )}
            />
            <TextField
              label="Comments"
              fullWidth
              multiline
              rows={2}
              value={formData.comments || ""}
              onChange={(e) => set("comments", e.target.value)}
            />
          </Box>
        )}

        {/* END RUN */}
        {type === "endRun" && (
          <Box sx={{ mt: 1 }}>
            <Typography gutterBottom>
              Are you sure you want to end the current production run for{" "}
              <strong>{machine.machineName}</strong>?
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Current total produced:{" "}
              <strong>{stats.activeRun?.totalProducedQuantity ?? 0}</strong>{" "}
              units
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Product: <strong>{stats.product || "N/A"}</strong>
            </Typography>
            <TextField
              label="End Time"
              type="time"
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: minTime, max: maxTime }}
              value={formData.endTime || ""}
              onChange={(e) => set("endTime", e.target.value)}
              sx={{ mt: 2 }}
            />
          </Box>
        )}

        {/* LOGS */}
        {type === "logs" && (
          <Box sx={{ mt: 1 }}>
            {runsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            ) : recentRuns.length === 0 ? (
              <Alert severity="info">No recent production runs found.</Alert>
            ) : (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ maxHeight: 400 }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell>Start Time</TableCell>
                      <TableCell>End Time</TableCell>
                      <TableCell align="right">Good</TableCell>
                      <TableCell align="right">Reject</TableCell>
                      <TableCell align="right">Rework</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentRuns.map((run) => (
                      <TableRow key={run.id || run.runId} hover>
                        <TableCell>
                          <Chip
                            label={run.status}
                            size="small"
                            color={
                              run.status === "Active" ? "success" : "default"
                            }
                            variant={
                              run.status === "Active" ? "filled" : "outlined"
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(run.startTime).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {run.endTime
                            ? new Date(run.endTime).toLocaleString()
                            : "—"}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: "bold", color: "success.main" }}
                        >
                          {run.totalProducedQuantity}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color:
                              run.totalRejects > 0 ? "error.main" : "inherit",
                          }}
                        >
                          {run.totalRejects}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color:
                              run.totalRework > 0 ? "warning.main" : "inherit",
                          }}
                        >
                          {run.totalRework}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* EDIT LOG */}
        {type === "editLog" && (
          <Box sx={{ mt: 1 }}>
            {editError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditError(null)}>
                {editError}
              </Alert>
            )}
            {editLogLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            ) : editLogRuns.length === 0 ? (
              <Alert severity="info">No production runs found for this shift/date.</Alert>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {editLogRuns.map((run) => (
                  <Box key={run.id || run.runId} sx={{
                    border: "1px solid",
                    borderColor: "grey.200",
                    borderRadius: 2,
                    overflow: "hidden"
                  }}>
                    {/* Run Header */}
                    <Box sx={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      px: 2, py: 1.5, bgcolor: "grey.50", borderBottom: "1px solid", borderColor: "grey.200"
                    }}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          Run #{run.id || run.runId} — {run.productName || "N/A"}
                          {(run.skuCode || run.SkuCode) && (
                            <span className="ml-2 px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 border border-teal-200 text-[10px]">
                              {run.skuCode || run.SkuCode} {(run.cycleTime || run.CycleTime) ? `(${Number(run.cycleTime || run.CycleTime).toFixed(1)}s)` : ''}
                            </span>
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {run.startTime ? new Date(run.startTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                          {" → "}
                          {run.endTime ? new Date(run.endTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "Active"}
                        </Typography>
                      </Box>
                      {editingRun?.run?.id === (run.id || run.runId) ? (
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button size="small" onClick={() => setEditingRun(null)} disabled={editSaving}>
                            Cancel
                          </Button>
                          <Button size="small" variant="contained" onClick={handleSaveRunEdit} disabled={editSaving}>
                            {editSaving ? <CircularProgress size={14} /> : "Save"}
                          </Button>
                        </Box>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => setEditingRun({
                            run,
                            fields: {
                              totalProducedQuantity: run.totalProducedQuantity ?? 0,
                              rejectQuantity: run.totalRejects ?? 0,
                              reworkQuantity: run.totalRework ?? 0,
                            }
                          })}
                          title="Edit production data"
                        >
                          <HiOutlinePencil size={16} />
                        </IconButton>
                      )}
                    </Box>

                    {/* Production Edit Form */}
                    {editingRun?.run?.id === (run.id || run.runId) ? (
                      <Box sx={{ display: "flex", gap: 2, p: 2, flexWrap: "wrap", bgcolor: "amber.50" }}>
                        <TextField
                          label="Good Qty"
                          type="number"
                          size="small"
                          inputProps={{ min: 0 }}
                          value={editingRun.fields.totalProducedQuantity}
                          onChange={(e) => setEditingRun(prev => ({ ...prev, fields: { ...prev.fields, totalProducedQuantity: e.target.value } }))}
                          sx={{ width: 120 }}
                        />
                        <TextField
                          label="Rejects"
                          type="number"
                          size="small"
                          inputProps={{ min: 0 }}
                          value={editingRun.fields.rejectQuantity}
                          onChange={(e) => setEditingRun(prev => ({ ...prev, fields: { ...prev.fields, rejectQuantity: e.target.value } }))}
                          sx={{ width: 120 }}
                        />
                        <TextField
                          label="Rework"
                          type="number"
                          size="small"
                          inputProps={{ min: 0 }}
                          value={editingRun.fields.reworkQuantity}
                          onChange={(e) => setEditingRun(prev => ({ ...prev, fields: { ...prev.fields, reworkQuantity: e.target.value } }))}
                          sx={{ width: 120 }}
                        />
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", gap: 3, px: 2, py: 1.5 }}>
                        <Typography variant="body2"><strong>Good:</strong> {run.totalProducedQuantity ?? 0}</Typography>
                        <Typography variant="body2" color="error.main"><strong>Rejects:</strong> {run.totalRejects ?? 0}</Typography>
                        <Typography variant="body2" color="warning.main"><strong>Rework:</strong> {run.totalRework ?? 0}</Typography>
                      </Box>
                    )}

                    {/* Downtime Logs for this run */}
                    {run.downtimeLogs && run.downtimeLogs.length > 0 && (
                      <Box sx={{ borderTop: "1px solid", borderColor: "grey.100" }}>
                        <Typography variant="caption" sx={{
                          px: 2, py: 1, display: "block", fontWeight: "bold",
                          color: "text.secondary", bgcolor: "grey.50", letterSpacing: 1, textTransform: "uppercase"
                        }}>
                          Downtime Logs
                        </Typography>
                        {run.downtimeLogs.map((log) => (
                          <Box key={log.id} sx={{
                            px: 2, py: 1.5, borderTop: "1px solid", borderColor: "grey.100"
                          }}>
                            {editingDowntime?.log?.id === log.id ? (
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {log.startTime ? new Date(log.startTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                                    {" → "}
                                    {log.endTime ? new Date(log.endTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                                    {" ("}{Number(log.durationMinutes).toFixed(1)}{" min)"}
                                  </Typography>
                                </Box>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Loss Type</InputLabel>
                                  <Select
                                    value={editingDowntime.fields.lossTypeId || ""}
                                    label="Loss Type"
                                    onChange={(e) => {
                                      const typeId = e.target.value;
                                      const subTypes = lossSubTypes.filter(s => s.lossTypeId == typeId || s.LossTypeId == typeId);
                                      const reasons = typeId ? lossReasons.filter(r => r.lossTypeId == typeId || r.LossTypeId == typeId) : [];
                                      setEditingDowntime(prev => ({
                                        ...prev,
                                        fields: { ...prev.fields, lossTypeId: typeId, lossSubTypeId: "", lossReasonId: "", lossReasonInput: "" },
                                        filteredSubTypes: subTypes,
                                        filteredReasons: reasons,
                                      }));
                                    }}
                                  >
                                    <MenuItem value="">None</MenuItem>
                                    {lossTypes.map((t) => (
                                      <MenuItem key={t.lossTypeId || t.id} value={t.lossTypeId || t.id}>
                                        {t.lossTypeName || t.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <FormControl size="small" fullWidth disabled={!editingDowntime.fields.lossTypeId || (editingDowntime.filteredSubTypes || []).length === 0}>
                                  <InputLabel>Loss Sub-Type</InputLabel>
                                  <Select
                                    value={editingDowntime.fields.lossSubTypeId || ""}
                                    label="Loss Sub-Type"
                                    onChange={(e) => {
                                      const subId = e.target.value;
                                      const reasons = subId
                                        ? lossReasons.filter(r => r.lossSubTypeId == subId || r.LossSubTypeId == subId)
                                        : (editingDowntime.fields.lossTypeId ? lossReasons.filter(r => r.lossTypeId == editingDowntime.fields.lossTypeId || r.LossTypeId == editingDowntime.fields.lossTypeId) : []);
                                      setEditingDowntime(prev => ({
                                        ...prev,
                                        fields: { ...prev.fields, lossSubTypeId: subId, lossReasonId: "", lossReasonInput: "" },
                                        filteredReasons: reasons,
                                      }));
                                    }}
                                  >
                                    <MenuItem value="">None</MenuItem>
                                    {(editingDowntime.filteredSubTypes || []).map((s) => (
                                      <MenuItem key={s.lossSubTypeId || s.id} value={s.lossSubTypeId || s.id}>
                                        {s.lossSubTypeName || s.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <Autocomplete
                                  freeSolo
                                  size="small"
                                  disabled={!editingDowntime.fields.lossTypeId}
                                  options={editingDowntime.filteredReasons || []}
                                  getOptionLabel={(option) => {
                                    if (typeof option === 'string') return option;
                                    return option.lossReasonName || option.name || option.reason || "";
                                  }}
                                  value={(editingDowntime.filteredReasons || []).find(r => (r.lossReasonId || r.id) == editingDowntime.fields.lossReasonId) || editingDowntime.fields.lossReasonInput || ""}
                                  onChange={(event, newValue) => {
                                    if (typeof newValue === 'string') {
                                      const match = (editingDowntime.filteredReasons || []).find(r => (r.lossReasonName || r.name || r.reason) === newValue);
                                      if (match) {
                                        setEditingDowntime(prev => ({
                                          ...prev,
                                          fields: { ...prev.fields, lossReasonId: match.lossReasonId || match.id, lossReasonInput: match.lossReasonName || match.name || match.reason }
                                        }));
                                      } else {
                                        setEditingDowntime(prev => ({
                                          ...prev,
                                          fields: { ...prev.fields, lossReasonId: "", lossReasonInput: newValue }
                                        }));
                                      }
                                    } else if (newValue) {
                                      setEditingDowntime(prev => ({
                                        ...prev,
                                        fields: { ...prev.fields, lossReasonId: newValue.lossReasonId || newValue.id, lossReasonInput: newValue.lossReasonName || newValue.name || newValue.reason }
                                      }));
                                    } else {
                                      setEditingDowntime(prev => ({
                                        ...prev,
                                        fields: { ...prev.fields, lossReasonId: "", lossReasonInput: "" }
                                      }));
                                    }
                                  }}
                                  onInputChange={(event, newInputValue) => {
                                    const match = (editingDowntime.filteredReasons || []).find(r => (r.lossReasonName || r.name || r.reason) === newInputValue);
                                    setEditingDowntime(prev => ({
                                      ...prev,
                                      fields: { 
                                        ...prev.fields, 
                                        lossReasonId: match ? (match.lossReasonId || match.id) : "",
                                        lossReasonInput: newInputValue 
                                      }
                                    }));
                                  }}
                                  renderInput={(params) => (
                                    <TextField {...params} label="Loss Reason" size="small" />
                                  )}
                                />
                                <TextField
                                  label="Comments"
                                  size="small"
                                  fullWidth
                                  multiline
                                  rows={2}
                                  value={editingDowntime.fields.comments || ""}
                                  onChange={(e) => setEditingDowntime(prev => ({ ...prev, fields: { ...prev.fields, comments: e.target.value } }))}
                                />
                                <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                                  <Button size="small" onClick={() => setEditingDowntime(null)} disabled={editSaving}>
                                    Cancel
                                  </Button>
                                  <Button size="small" variant="contained" color="primary" onClick={handleSaveDowntimeEdit} disabled={editSaving}>
                                    {editSaving ? <CircularProgress size={14} /> : "Save Changes"}
                                  </Button>
                                </Box>
                              </Box>
                            ) : (
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" fontWeight="medium">
                                    {log.lossTypeName || "No Loss Type"}
                                    {log.lossSubTypeName ? ` › ${log.lossSubTypeName}` : ""}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {log.startTime ? new Date(log.startTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                                    {" → "}
                                    {log.endTime ? new Date(log.endTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                                    {" · "}
                                    {Number(log.durationMinutes).toFixed(1)} min
                                    {log.comments ? ` · ${log.comments}` : ""}
                                  </Typography>
                                </Box>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const initSubTypes = log.lossTypeId
                                      ? lossSubTypes.filter((s) => s.lossTypeId == log.lossTypeId || s.LossTypeId == log.lossTypeId)
                                      : [];
                                    const initReasons = log.lossSubTypeId
                                      ? lossReasons.filter((r) => r.lossSubTypeId == log.lossSubTypeId || r.LossSubTypeId == log.lossSubTypeId)
                                      : log.lossTypeId
                                      ? lossReasons.filter((r) => r.lossTypeId == log.lossTypeId || r.LossTypeId == log.lossTypeId)
                                      : [];

                                    setEditingDowntime({
                                      log,
                                      fields: {
                                        lossTypeId: log.lossTypeId || "",
                                        lossSubTypeId: log.lossSubTypeId || "",
                                        lossReasonId: log.lossReasonId || "",
                                        lossReasonInput: log.lossReasonName || "",
                                        comments: log.comments || "",
                                      },
                                      filteredSubTypes: initSubTypes,
                                      filteredReasons: initReasons,
                                    });
                                  }}
                                  title="Edit downtime log"
                                >
                                  <HiOutlinePencil size={15} />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={submitting}>
          Cancel
        </Button>
        {type !== "logs" && type !== "editLog" && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            color={type === "endRun" ? "warning" : "primary"}
            disabled={submitting}
          >
            {submitting ? (
              <CircularProgress size={20} />
            ) : type === "endRun" ? (
              "End Run"
            ) : (
              "Confirm"
            )}
          </Button>
        )}
        {type === "editLog" && (
          <Button onClick={() => onClose(true)} variant="outlined">
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OperatorDashboard;
